import { useGetTravelCompensationType } from 'queries';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextFieldProps } from '@mui/material';

import { ControlledCheckbox, ControlledSelect } from 'components/shared';
import { Option } from 'components/shared/ControlledFields/ControlledSelect';
import TextField from 'components/shared/ControlledFields/ControlledTextField';

import { convertArrangementTypeToOption } from '../arrangementsUtils';
import { TransportationModeEnum } from 'RestAPIs/travel-distances/types';

interface FieldsProp {
  control: Control<any>;
}

export const CodeField: React.FC<FieldsProp & TextFieldProps> = ({
  control,
  ...props
}) => {
  const { t } = useTranslation('common');

  return (
    <TextField
      {...props}
      name="code"
      label={t('adminPortal.arrangements.code')}
      control={control}
      variant="outlined"
    />
  );
};

export const CodeExternField: React.FC<FieldsProp & TextFieldProps> = ({
  control,
  ...props
}) => {
  const { t } = useTranslation('common');

  return (
    <TextField
      {...props}
      name="codeExtern"
      label={t('adminPortal.arrangements.externalCode')}
      control={control}
      variant="outlined"
    />
  );
};

export const DescriptionField: React.FC<FieldsProp & TextFieldProps> = ({
  control,
  ...props
}) => {
  const { t } = useTranslation('common');

  return (
    <TextField
      {...props}
      name="description"
      label={t('adminPortal.arrangements.description')}
      control={control}
      variant="outlined"
    />
  );
};

export const CompensationTypeIdField: React.FC<
  FieldsProp & { disabled?: boolean }
> = (props) => {
  const { data: arrangementTypes } = useGetTravelCompensationType();

  return (
    <CompensationTypeIdFieldInner
      {...props}
      options={convertArrangementTypeToOption(arrangementTypes ?? [])}
      control={props.control}
    />
  );
};

export const CompensationTypeIdFieldInner: React.FC<
  FieldsProp & { disabled?: boolean; options: Option[] | [] }
> = ({ ...props }) => {
  const { t } = useTranslation('common');

  return (
    <ControlledSelect
      {...props}
      name="compensationTypeId"
      label={t('adminPortal.arrangements.typeOfArrangement')}
      options={props.options}
      variant="outlined"
    />
  );
};

export const TransportationModeField: React.FC<
  FieldsProp & { disabled?: boolean }
> = (props) => {
  const { t } = useTranslation('common');

  const options = Object.values(TransportationModeEnum).map((mode) => ({
    value: mode,
    label: t(`travelDistances.transportationModes.${mode}`)
  }));

  return (
    <TransportationModeFieldInner
      {...props}
      options={options ?? []}
      control={props.control}
    />
  );
};

export const TransportationModeFieldInner: React.FC<
  FieldsProp & { disabled?: boolean; options: Option[] | [] }
> = ({ ...props }) => {
  const { t } = useTranslation('common');

  return (
    <ControlledSelect
      {...props}
      name="transportationMode"
      label={t('travelDistances.transportationMode')}
      options={props.options}
      variant="outlined"
    />
  );
};

export const IsExternalField: React.FC<FieldsProp & { disabled?: boolean }> = ({
  control,
  ...props
}) => {
  const { t } = useTranslation('common');

  return (
    <ControlledCheckbox
      {...props}
      name="isExternal"
      label={t('adminPortal.arrangements.isExternal')}
      control={control}
    />
  );
};
